import { MessageBarType } from '@fluentui/react';
import { useToast } from '@h2oai/ui-kit';

import { EntityActionType } from '../../../aiem/entity/constants';
import { MessageText } from '../../../aiem/entity/types';
import { fetchWrapRPC, getAccessToken } from '../../../services/api';
import { GrpcStatusCode } from '../../../services/HTTPResponse';
import { handleErrMsg } from '../../../utils/utils';
import { EntitiesMap } from '../components/types';
import type { Entity } from './types';
import { checkForPrefix } from './utils';

// TODO: Merge these hooks to ./website/src/aiem/entity/hooks.ts
const completeMessageText = <EntityType extends string>(
  messageText: MessageText | undefined,
  entityType: EntityType,
  entitiesMap: EntitiesMap<EntityType>
) => {
  const sourceEntity = entitiesMap.get(entityType);

  if (!sourceEntity) return messageText;

  return {
    ...messageText,
    group: sourceEntity.displayName,
  };
};

const toText = ({ group, action, subject, object }: MessageText) =>
  `Settings ${(group || '').concat(String(group).includes(':') ? ' ' : ': ')} ${subject ? subject : ''} ${
    action || 'action occurred '
  } ${object || 'successfully'}`.replaceAll('::', ':');

type UseEntityParams<EntityType extends string> = {
  entitiesMap: EntitiesMap<EntityType>;
  requestPath: string;
};

export const useEntity = <EntityType extends string>(params: UseEntityParams<EntityType>) => {
  const { requestPath: basePath, entitiesMap } = params;
  const { addToast } = useToast();
  const requestConfig = { basePath, bearerToken: getAccessToken };
  const defaultErrorMessage = 'Admin Settings Error';

  const act = async <EntityModel, EntityRequest>(
    entity: Entity<EntityModel, EntityType>,
    type: EntityActionType,
    request: EntityRequest,
    messageText?: MessageText,
    suppressToast = false,
    abortController?: AbortController
  ) => {
    const { actions } = entity;
    const action = actions[type];
    const rpc = action?.rpc;
    const responseKey = action?.responseKey;
    const successfulMessageText = completeMessageText<EntityType>(messageText, entity.type, entitiesMap);

    if (rpc) {
      try {
        let response;
        if (abortController) {
          response = await fetchWrapRPC(rpc, requestConfig)(request, { signal: abortController.signal });
        } else {
          response = await fetchWrapRPC(rpc, requestConfig)(request);
        }
        if (!suppressToast) {
          addToast({
            message: toText(successfulMessageText || {}),
            messageBarType: MessageBarType.success,
          });
        }
        return response[responseKey || 'data'] as EntityModel[];
      } catch (error: any) {
        !suppressToast &&
          addToast({
            message: `${defaultErrorMessage}: ${handleErrMsg(error.message)}`,
            messageBarType: MessageBarType.error,
          });
        throw error;
      }
    }
    return undefined;
  };
  const create = async <EntityModel, EntityRequest>(
    e: Entity<EntityModel, EntityType>,
    req: EntityRequest,
    messageText?: MessageText,
    abortController?: AbortController
  ) => await act(e, EntityActionType.Create, req, messageText, false, abortController);
  const erase = async <EntityModel, EntityRequest>(
    e: Entity<EntityModel, EntityType>,
    req: EntityRequest,
    messageText?: MessageText,
    abortController?: AbortController
  ) => await act(e, EntityActionType.Delete, req, messageText, false, abortController);
  const get = async <EntityModel, EntityRequest>(
    e: Entity<EntityModel, EntityType>,
    req: EntityRequest,
    suppressToast = false,
    abortController?: AbortController
  ) => {
    const { requestNameKeyPrefix } = e;
    const name = checkForPrefix((req as unknown as { name: string }).name, requestNameKeyPrefix);
    return await act(e, EntityActionType.Get, { ...req, name }, undefined, suppressToast, abortController);
  };
  const list = async <EntityModel, EntityRequest>(
    e: Entity<EntityModel, EntityType>,
    req: EntityRequest,
    abortController?: AbortController
  ) => {
    return await act(e, EntityActionType.List, req, undefined, true, abortController);
  };
  const update = async <EntityModel, EntityRequest>(
    e: Entity<EntityModel, EntityType>,
    req: EntityRequest,
    messageText?: MessageText,
    abortController?: AbortController
  ) =>
    await act(
      e,
      EntityActionType.Update,
      { ...req, updateMask: (req as any).updateMask || '*' },
      messageText,
      false,
      abortController
    );
  const reorder = async <EntityModel, EntityRequest>(
    e: Entity<EntityModel, EntityType>,
    req: EntityRequest,
    messageText?: MessageText,
    abortController?: AbortController
  ) => await act(e, EntityActionType.Reorder, req, messageText, false, abortController);
  const assign = async <EntityModel>(
    e: Entity<EntityModel, EntityType>,
    model: EntityModel,
    messageText?: MessageText,
    suppressToast = true,
    abortController?: AbortController
  ) => {
    const assignAction = e.actions.Assign;
    if (Boolean(assignAction)) {
      const { requestNameKey, requestPayloadKey, payloadKey } = assignAction || {
        requestNameKey: 'key',
        requestPayloadKey: 'payload',
      };
      let payloadValue: any;
      if (payloadKey) {
        payloadValue = model[payloadKey];
      }
      if (payloadValue) {
        const req = {
          [requestNameKey!]: checkForPrefix(model['name'], e.requestNameKeyPrefix),
          [requestPayloadKey!]: payloadValue,
        };
        return await act(
          e,
          EntityActionType.Assign,
          req,
          {
            ...messageText,
            action: messageText?.action || `${String(requestPayloadKey)} assigned `,
          },
          suppressToast,
          abortController
        );
      }
    }
    return undefined;
  };
  const setLatest = async <EntityModel>(
    e: Entity<EntityModel, EntityType>,
    model: EntityModel,
    suppressToast = false,
    abortController?: AbortController
  ) => {
    const field = 'aliases',
      withLatest = ['latest'];
    const aliases = model[field] && Array.isArray(model[field]) ? [...model[field], ...withLatest] : withLatest;
    const newModel = { ...model, aliases };
    return await assign(
      e,
      newModel,
      { subject: model['version'] || model['displayName'] || model['name'], action: 'has been set as latest ' },
      suppressToast,
      abortController
    );
  };
  const checkId = async (entityType: EntityType, name: string, abortController?: AbortController) => {
    const entity = entitiesMap.get(entityType) as Entity<unknown, EntityType>;
    let available = false;
    try {
      const model = await get(entity, { name: `${name}` }, true, abortController);
      available = !Boolean(model);
    } catch (error: any) {
      if (error.message.includes('not found') || error.code === GrpcStatusCode.NOT_FOUND) {
        available = true;
      }
    }
    return available;
  };

  return { assign, checkId, create, erase, get, list, update, reorder, setLatest };
};
